import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ArrowLeft } from 'lucide-react'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { subscriptionApi, SubscriptionStatus } from '../services/api'
import { resetMonthUsageCache } from '../shell/useMonthUsage'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Inline,
  LoadingState,
  Stack,
  Stat,
  TileGrid,
  Toggle,
} from '../ds'

type BillingInterval = 'month' | 'year'
type TierId = 'starter' | 'scale' | 'enterprise'

// Per-tier marketing copy — mirrors the marketing site pricing page.
// Styling is the design system's (MAR-372): Card, Stat, Toggle, Button.
const TIER_COPY: Record<string, { description: string; mauLabel?: string; features: string[] }> = {
  starter: {
    description: 'For stores building their customer base. The same shop assistant — for a smaller shop.',
    features: [
      'Chat, email & SMS (your Twilio)',
      'Full memory, governance & attribution',
      'The same AI as Scale'
    ]
  },
  scale: {
    description: 'For stores with a busier shop floor. The same shop assistant — for a bigger one.',
    features: [
      'Chat, email & SMS (your Twilio)',
      'Full memory, governance & attribution',
      'The same AI as Starter'
    ]
  },
  enterprise: {
    description: 'For high-volume shops with custom requirements. Annual contract, dedicated support.',
    mauLabel: '10,000+',
    features: [
      'Custom usage limits',
      'Dedicated success manager',
      'SLA guarantee',
      'Annual contract'
    ]
  }
}

const PricingView = () => {
  const navigate = useNavigate()
  const { currentWorkspace } = useWorkspaceStore()
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year')
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState<TierId | null>(null)

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!currentWorkspace) return
      try {
        const response = await subscriptionApi.getStatus(currentWorkspace.accountId)
        if (response.success && response.data) {
          setSubscription(response.data)
        }
      } catch (err) {
        console.error('Failed to fetch subscription:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchSubscription()
  }, [currentWorkspace?.accountId])

  // Subscription state drives what each card's CTA means:
  // - a pure platform trial (source 'trial') can genuinely "start free"
  // - a live Stripe subscription switches plans in place (no new checkout)
  // - an enterprise/invoice account is managed by Marva — no self-serve changes
  const isEnterpriseAccount = subscription?.tier === 'enterprise'
  const hasPaidPlan = subscription?.source === 'stripe' || subscription?.source === 'invoice'
  const hasLiveStripeSub = subscription?.source === 'stripe' &&
    !!subscription.stripeSubscriptionId &&
    ['active', 'past_due', 'trialing'].includes(subscription.status)
  // "Start free" is only truthful while the trial is genuinely live. An expired
  // trial (status 'expired', still no paid plan) has to subscribe — keying the
  // CTA off `source` alone left it saying "Start free" after the trial ended.
  const trialActive = !hasPaidPlan && subscription?.status === 'trialing'
  const trialExpired = !hasPaidPlan && subscription?.status === 'expired'

  const handleSelectPlan = async (tierId: TierId) => {
    if (!currentWorkspace || tierId === 'enterprise' || isEnterpriseAccount) return

    setCheckoutLoading(tierId)
    try {
      const response = await subscriptionApi.createCheckout(currentWorkspace.accountId, {
        tier: tierId,
        billingInterval,
        successUrl: `${window.location.origin}/settings?upgrade=success`,
        cancelUrl: `${window.location.origin}/pricing`
      })
      if (response.success && response.data?.checkoutUrl) {
        window.location.assign(response.data.checkoutUrl)
      } else if (response.success && response.data?.updated) {
        // Plan switched in place (prorated) — no Stripe redirect needed. The profile menu's
        // cached usage still names the old plan; drop it so the next open reads the new one.
        resetMonthUsageCache()
        navigate('/billing')
      }
    } catch (err) {
      console.error('Checkout error:', err)
    } finally {
      setCheckoutLoading(null)
    }
  }

  if (loading) {
    return <LoadingState />
  }

  const tiers = subscription?.availableTiers || []
  const discountPercent = subscription?.annualDiscountPercent || 20

  return (
    <div className="mv-canvas-wide">
      <Stack gap="lg">
        <div>
          <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        <Stack gap="sm">
          <p className="t-lead">
            Pricing scales with monthly active customers — the people Marva actually talks to. Not your list size, not
            tier-locked features. The same shop assistant at either size.
          </p>
          {trialActive && <p className="t-small">14-day free trial. No credit card required.</p>}
          {trialExpired && (
            <Alert tone="attention">Your free trial has ended — choose a plan to reactivate Marva.</Alert>
          )}
        </Stack>

        {/* Billing interval */}
        <Inline>
          <span className="t-small">Monthly</span>
          <Toggle
            label="Bill annually"
            labelHidden
            checked={billingInterval === 'year'}
            onChange={(annual) => setBillingInterval(annual ? 'year' : 'month')}
          />
          <span className="t-small">Annual</span>
          <Badge tone="gold">Save {discountPercent}%</Badge>
        </Inline>

        {/* Pricing cards */}
        <TileGrid>
          {tiers.map((tier) => {
            // A platform trial defaults tier to 'starter' in the API response —
            // only a paid plan makes a tier "current" (a trialing account must
            // still be able to subscribe to Starter)
            const isCurrentTier = hasPaidPlan && subscription?.tier === tier.id
            const isPopular = tier.popular
            const isEnterprise = tier.contactSales
            const isLoading = checkoutLoading === tier.id
            const copy = TIER_COPY[tier.id] || { description: '', features: [] }
            const features = [
              ...(!isEnterprise && tier.limits?.conversations != null && tier.limits?.emails != null
                ? [`${tier.limits.conversations.toLocaleString()} conversations & ${tier.limits.emails.toLocaleString()} emails`]
                : []),
              ...copy.features,
            ]

            return (
              <Card key={tier.id} tone={isPopular ? 'gold' : 'plain'} padding="lg">
                <Stack>
                  <CardHeader
                    title={tier.name}
                    description={copy.description}
                    aside={isPopular ? <Badge tone="solid">Most chosen</Badge> : undefined}
                  />

                  {/* Price */}
                  {isEnterprise ? (
                    <Stat value="Custom" label="Annual contract" />
                  ) : billingInterval === 'year' ? (
                    <Stat
                      tone="gold"
                      value={(tier.annualMonthlyPrice || tier.monthlyPrice || '—').replace('/mo', '')}
                      label={
                        <>
                          /month · <s>{tier.monthlyPrice}/mo</s> billed annually
                        </>
                      }
                    />
                  ) : (
                    <Stat tone="gold" value={tier.monthlyPrice} label="/month · billed monthly" />
                  )}

                  {/* MAU — optional-chained so a stale/partial API response
                      degrades to a dash instead of crashing the page */}
                  <Stat
                    value={copy.mauLabel || tier.limits?.mau?.toLocaleString() || '—'}
                    label="Monthly active customers"
                  />

                  {/* Features */}
                  <Stack gap="sm" role="list">
                    {features.map((feature) => (
                      <Inline key={feature} role="listitem">
                        <Check size={16} aria-hidden="true" />
                        <span className="t-small">{feature}</span>
                      </Inline>
                    ))}
                  </Stack>

                  {!isEnterprise && <p className="t-hint">Overage $0.05/conv · $1/1K emails</p>}
                </Stack>

                <CardFooter>
                  {isCurrentTier ? (
                    <Button variant="ghost" block disabled>
                      Current plan
                    </Button>
                  ) : isEnterprise ? (
                    <Button variant="ghost" block href="mailto:sales@marva.co?subject=Enterprise%20Inquiry">
                      Talk to us
                    </Button>
                  ) : isEnterpriseAccount ? (
                    // Enterprise accounts can't self-serve onto a paid tier —
                    // checkout would clobber the negotiated subscription
                    <Button variant="ghost" block href="mailto:support@marva.co?subject=Plan%20change">
                      Contact support
                    </Button>
                  ) : (
                    <Button
                      variant={isPopular ? 'gold' : 'ghost'}
                      block
                      busy={isLoading}
                      busyLabel="Opening checkout…"
                      onClick={() => handleSelectPlan(tier.id as TierId)}
                    >
                      {trialActive ? 'Start free' : hasLiveStripeSub ? `Switch to ${tier.name}` : 'Subscribe'}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            )
          })}
        </TileGrid>

        {/* Footer info */}
        <Stack gap="sm">
          <p className="t-small">
            SMS goes through your own Twilio account — you pay Twilio directly, we never mark it up.
          </p>
          <p className="t-small">
            Questions? <a href="mailto:support@marva.co">Contact us</a>
          </p>
        </Stack>
      </Stack>
    </div>
  )
}

export default PricingView
